import moment, * as Moment from 'moment';
import { StringHelpers } from '../../helpers/StringHelpers';
import { DateParser } from '@fnv-core/parsing/date/DateParser';
import { FeatureFactory } from '../components/features/factories/FeatureFactory';
import { AdresZoeker } from '../components/features/AdresZoeker';
import { FormControlMapping } from '../components/FormControlMapping';

export class ValidationControlHelper {

  public static getErrorMessageWithReplacementFields = (errorMessage: string, field: Field, rule: any) : string => {
    if(!errorMessage) {
      return "";
    }

    const replacementFields = {
      fieldName : field?.properties?.fieldcaption
    };

    if(rule) {
      replacementFields["min"] = rule.MinimumLength;
      replacementFields["max"] = rule.MaximumLength;
    }

    return StringHelpers.replacePropertiesInString(errorMessage, replacementFields, true);
  }

  public static validateField = (fields: Field[], fieldName: string, formData: any, setFormData: any, handleNewFormData: any, isRequired: boolean, force: boolean = false) : ValidationError[] => {

    let formDataEntry = formData[fieldName];
    const field = fields.find(f => f["@column"] === fieldName);
    
    if(!field) {
      return null;
    }

    if(!formDataEntry) {
      formDataEntry = { value: FormControlMapping.GetComponentTypedValue(field.properties.defaultvalue, field.settings.componentidentifier) ?? null, focus: false, touched: field.properties.defaultvalue ? true : force, enabled: true, loading: false };
      formDataEntry = ValidationControlHelper.initFeatureField(field, formDataEntry, fields);
      handleNewFormData(fieldName, formDataEntry.value, formDataEntry.focus, formDataEntry.touched, formDataEntry.enabled);

      if(!force) {
        return [];
      }
    }

    if(force && !formDataEntry.touched) {
      formDataEntry.touched = true;
      handleNewFormData(fieldName, undefined, formDataEntry.focus, formDataEntry.touched, formDataEntry.enabled);
    }
    
    if(!formDataEntry.touched) {
      return [];
    }

    // handle required
    if(isRequired && !ValidationControlHelper.validateDataByRule('required', null, formDataEntry?.value)) {
      const error = StringHelpers.formFieldRequiredFieldErrorMessage(field.properties.fieldcaption);
      return [{ message: error, html: undefined }];
    }

    // handle field type specific validation
    if(field.settings?.validationerrormessage && !ValidationControlHelper.validateDataByControlName(field.settings.controlname, formDataEntry?.value)) {
      return [{
        message: ValidationControlHelper.getErrorMessageWithReplacementFields(field.settings.validationerrormessage, field, null),
        html: undefined 
      }];
    }

    // handle feature fields validation
    const featureErrors = ValidationControlHelper.validateFeatureField(field, formDataEntry?.value, formData, setFormData);
    if(featureErrors) {
      return [{
        message: ValidationControlHelper.getErrorMessageWithReplacementFields(featureErrors, field, null),
        html: undefined 
      }];
    }

    // handle validationrulesdata
    const validationObject = field.validationrulesdata;
    if(!validationObject) {
      return [];
    }
    
    // more than 1 validation
    if(Array.isArray(validationObject.ValidationRuleConfiguration)) {
      const result = validationObject.ValidationRuleConfiguration.filter((item) => !ValidationControlHelper.validateDataByRule(item.Identifier, item.ValidationRule, formDataEntry?.value));
      return result?.map((item) => { return { 
          message: ValidationControlHelper.getErrorMessageWithReplacementFields(item.ValidationRule.ErrorMessage, field, item.ValidationRule),
          html: item.ValidationRule.Html
        }; 
      });
    }
    else {
      // only 1 validation
      const isValid = ValidationControlHelper.validateDataByRule(validationObject.ValidationRuleConfiguration.Identifier, validationObject.ValidationRuleConfiguration.ValidationRule, formDataEntry?.value);
      return isValid ? [] : [{ 
          message: ValidationControlHelper.getErrorMessageWithReplacementFields(validationObject.ValidationRuleConfiguration.ValidationRule.ErrorMessage, field, validationObject.ValidationRuleConfiguration.ValidationRule),
          html: validationObject.ValidationRuleConfiguration.ValidationRule.Html
        }
      ];
    }
  }

  public static validateDataByControlName = (controlName: any, value: any) : boolean => {
    switch (controlName) {
      case 'internationalphone':
          const phonePattern = /^[0-9]*$/;
          return phonePattern.test(value);
      default:
        return true;
    }
  }

  public static validateFeatureField = (field: Field, value: any, formData: any, setFormData: any) : string => {
    // By inputcontrolstyle
    let error = null;
    switch (field.properties.inputcontrolstyle) {
      case 'adreszoeker':
        error = new AdresZoeker(formData, setFormData).validateField(field["@column"], value);
      default: 
        break;
    }
  
    return error;
  };

  public static validateDataByRule = (ruleName: string, rule: any, value: any) : boolean => {
    switch (ruleName) {
      case 'required':
        return (value !== undefined && value !== null && value !== "" && !(typeof value === "boolean")) || ((typeof value === "boolean") && value == true); 
      case 'Kentico.Email':
        const emailPattern = /^[a-zA-Z0-9._+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
        return emailPattern.test(value);
      case 'Kentico.RegularExpression':
        const regex = new RegExp(rule.RegularExpression);
        return regex.test(value);
      case 'Kentico.MinimumLength':
          return (value?.length ?? 0) >= parseInt(rule.MinimumLength);
      case 'Kentico.MaximumLength':
        return !((value?.length ?? 0) > parseInt(rule.MaximumLength));
      case 'CalendarMinYearsValidationRule':
        return ValidationControlHelper.dateIsValid(new DateParser().parse(value), parseInt(rule.MinYearsValue)); 
      case 'CalendarDateNotBetweenDatesValidationRule':
        return !ValidationControlHelper.dateIsValid(new DateParser().parse(value), parseInt(rule.MinYearsValue), parseInt(rule.MaxYearsValue)); 
      case 'IbanValidationRule':
        return ValidationControlHelper.stringIsIban(value); 
      default:
        return true;
    }
  }

  public static valueIsWhatBoolean = (value: any) : boolean => {
    if(value == null) {
      return false;
    }

    return (value?.toString() ?? '').toLowerCase() == "true";
  }

  public static dateIsValid = (date: Date, minYear: number, maxYear: number = -1) : boolean => {
  
    const startDate = moment(date);
    const timeEnd = moment(new Date());
    const diff = timeEnd.diff(startDate); 
    const diffDuration = moment.duration(diff);

    if(maxYear == -1) {
      return diffDuration.years() >= minYear;
    }

    return diffDuration.years() >= minYear && diffDuration.years() < maxYear;
  }

  public static stringIsIban = (iban: string) : boolean => {
    const ibanRegex: RegExp = /^[A-Z]{2}[0-9A-Z]{13,32}$/;
    if (!iban || typeof iban !== "string") return false;

    iban = iban.toUpperCase().replace(/\s+/g, ''); // Remove spaces and ensure uppercase.

    // Check if the IBAN matches the generic IBAN format (2 letters + 13 to 32 alphanumeric characters).
    if (!ibanRegex.test(iban)) return false;

    // Move the first four characters to the end of the string.
    const rearrangedIban = iban.slice(4) + iban.slice(0, 4);

    // Replace letters with numbers (A = 10, B = 11, ..., Z = 35).
    const numericIban = rearrangedIban
      .split('')
      .map((char) => (/[A-Z]/.test(char) ? (char.charCodeAt(0) - 'A'.charCodeAt(0) + 10).toString() : char))
      .join('');

    // Perform the modulo 97 operation.
    const ibanBigInt = BigInt(numericIban);
    return ibanBigInt % BigInt(97) === BigInt(1);
  }

  public static isNullOrEmpty = (value: any) : boolean => {
    if (value == null || (typeof value === 'string' && value.trim() === '') || (Array.isArray(value) && value.length === 0) ) {
      return true;
    }

    return false;
  }

  public static initFeatureField = (field: Field, formDataEntry: FormDataEntry, fields: Field[]) : FormDataEntry => {

    const feature = FeatureFactory.getFeatureForField(field);

    if (feature) {
      formDataEntry = feature.initFeatureField(field, formDataEntry, fields);
    }
    
    return formDataEntry;
  };
}